import { EditorState, type Extension, StateEffect } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { useEffect, useEffectEvent, useRef } from "react";

/**
 * Manages a CodeMirror EditorView attached to a container ref.
 *
 * Creates the view *without* a parent so the DOM and styles are fully
 * initialized before anything is appended to the document, avoiding a
 * flash of unstyled content.
 */
export function useCodeMirror(
  containerRef: React.RefObject<HTMLDivElement | null>,
  value: string,
  extensions: Extension[]
) {
  // Shared across mount, reconfigure, and update effects.
  const viewRef = useRef<EditorView | null>(null);

  const createView = useEffectEvent(
    () =>
      new EditorView({
        state: EditorState.create({ doc: value, extensions }),
      })
  );

  // Mount / unmount
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const view = createView();
    container.appendChild(view.dom);
    viewRef.current = view;

    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, [containerRef]);

  // Reconfigure extensions
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({ effects: StateEffect.reconfigure.of(extensions) });
  }, [extensions]);

  // Update document by replacing only the differing suffix (common-prefix
  // diff) so CodeMirror preserves scroll position and selection across
  // streaming appends or minor late-arriving rewrites.
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const current = view.state.doc.toString();
    if (current === value) return;
    const limit = Math.min(current.length, value.length);
    let common = 0;
    while (
      common < limit &&
      current.charCodeAt(common) === value.charCodeAt(common)
    ) {
      common++;
    }
    view.dispatch({
      changes: {
        from: common,
        to: current.length,
        insert: value.slice(common),
      },
    });
  }, [value]);

  return viewRef;
}
